import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';

import {
  Box,
  FlexContainer,
  InputStyled,
  Text,
  TextAreaStyled,
} from '../../../../shared/Typhography';
import {
  fetchPayoutById,
  fetchPayouts,
  FilteringItem,
  getArtists,
  getMembers,
  timeStampToDate,
  timeStampToTime,
  updatePayout,
} from '../../../../services';
import { Button, Splash } from '../../../../shared/Components';
import { MODAL_TYPES, useGlobalModalContext } from '../../../../context/GlobalModal';
import { strings } from '../../../../strings';

import FlexebleTable from './Components/FlexebleTable';
import PaginationControle from './Components/PaginationControle';
import FilterControle from './Components/FilterControle';
import ActionsControle from './Components/ActionsControle';

const StatusBadge = styled.span`
  color: white;
  padding: 5px;
  border-radius: 4px;
  width: max-content;

  &.requested {
    background: orange;
  }
  &.pending {
    background: #d5d55f;
  }
  &.approved {
    background: #30af30;
  }
  &.rejected {
    background: red;
  }
  &.completed {
    background: #30af30;
  }
`;

const CellHoverInfo = styled.div`
  position: absolute;
  padding: 10px;
  background: white;
  z-index: 1;
  box-shadow: 0px 0px 10px lightgrey;
  bottom: 100%;
  left: 50%;
`;

const STATUSES = [
  { value: 'requested', label: 'Requested' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'completed', label: 'Completed' },
];

const PayoutsScreen = () => {
  const queryParams = new URLSearchParams(window.location.search);

  // When it is equal to payout's id it helps with openning payout's details modal.
  const view = queryParams.get('view');

  //State to check should allow fetching the initial fetch after reading from url.
  const [readFromUrlDone, setReadFromUrlDone] = useState(false);

  // Contains order by information, as data can be ordered by multiple parameters.
  // Default ordering is by request date (creation date) and status.
  const [ordering, setOrdering] = React.useState<string[]>(['createdAt']);

  // Data can be filtered by artist, status, who requested payout and date range.
  // This filter state is used inside of returned JSX.
  const [filtersState, setFiltersState] = React.useState<any>({});
  const [pagination, setPagination] = React.useState<any>({
    offset: 0,
    limit: {
      value: `${10}`,
      label: `${10} items`,
    },
    amount: 0,
  });

  // These filters are used only for function logic, like fetching and setting data.
  const [filters, setFilters] = React.useState<FilteringItem[]>([]);

  const [payouts, setPayouts] = React.useState<any>({});
  const [isLoadingData, setIsLoadingData] = React.useState<boolean>(false);
  const { showModal } = useGlobalModalContext();

  // State of payout's actions
  const [collapseState, setCollapseState] = useState<any>();

  // Needed for Artist dropdown (select) filters.
  const { data: artistsData, isLoading: isLoadingArtist } = getArtists();

  // Neeeded for Requester dropdown (select) filters.
  const { data: adminsData, isLoading: isLoadingAdmins } = getMembers();

  const [filtersOptions, setFilterOptions] = useState<any>([]);
  const [tableStates, setTableStates] = useState<{ hover: string[] }>({ hover: [] });

  const history = useHistory();
  const approveAmountElem = useRef(null);
  const reasoningElem = useRef(null);

  /**
   * Manages data's "order by" state.
   * If item has "-" in front of it, order is descending, and vice-versa.
   *
   * @param orderItem
   * @returns
   */
  const manageOrdering = (orderItem: string) => {
    setOrdering((prevState) => {
      const newState = [...prevState];
      if (newState.includes(orderItem) || newState.includes(`-${orderItem}`)) {
        const x = newState.map((value) => {
          if (value === orderItem || value === '-' + orderItem) {
            return value.startsWith('-') ? value.substr(1) : '-' + value;
          }
          return value;
        });
        return x;
      } else {
        newState.push(orderItem);
      }
      return newState;
    });
  };

  const fetch = async () => {
    setIsLoadingData(true);
    const res = await fetchPayouts({
      pagination: { offset: pagination?.offset, limit: +pagination?.limit?.value },
      ordering: ordering,
      filtering: filters,
    });

    setPayouts(res?.data);
    setIsLoadingData(false);
  };

  const refetch = async (id?: string) => {
    if (!id) {
      await fetch();
      return;
    }

    const freshData = await fetchPayoutById(id);

    setPayouts({
      ...payouts,
      results: payouts.results.map((item: any) => {
        return item?.uid != id ? item : freshData?.data;
      }),
    });
  };

  /**
   * Updates payout's status in firestore and refetches it to refresh its data.
   */
  const statusChange = async (
    uid: string,
    status: 'requested' | 'rejected' | 'pending' | 'approved' | 'completed',
    args?: any,
  ) => {
    try {
      await updatePayout({
        id: uid,
        status: status,
        ...args,
      });
      await refetch(uid);
      toast.success(`Status changed to ${status}.`);
    } catch (err) {
      toast.error(strings.FailedToChangeStatus);
    }
  };

  const getUsersData = () => {
    const artists: any = artistsData?.docs?.map((item) => {
      return { value: item?.id, label: item?.data()?.Name ?? item?.data()?.name };
    });
    const admins: any = adminsData?.docs?.map((item) => {
      return { value: item?.id, label: item?.data()?.Name ?? item?.data()?.name };
    });

    setFilterOptions({
      artists: artists,
      users: [...artists, ...admins],
    });
  };

  const addHistory = (id?: string) => {
    const filtersString = filters
      ?.map((filter) => `${filter?.attribute}=${filter?.value}`)
      .join('&');
    const paginationString = `offset=${pagination?.offset}&limit=${pagination?.limit?.value}`;
    const orderingString = ordering?.length > 0 ? 'ordering=' + ordering.join(',') : '';
    const viewString = id ? 'view=' + id : '';

    history.push(
      `/payouts?${[filtersString, paginationString, orderingString, viewString]
        .filter((val) => val?.length > 0)
        .join('&')}`,
    );
  };

  //Hook logic to pull from sorts from url.
  useEffect(() => {
    const ordering = queryParams.get('ordering');
    const offset = queryParams.get('offset');
    const limit = queryParams.get('limit');

    if (ordering) {
      setOrdering(ordering.split(','));
    }
    if (offset || limit) {
      setPagination((prevState: any) => ({
        ...prevState,
        offset: offset ? parseInt(offset) : 0,
        limit: {
          value: `${limit ?? 0}`,
          label: `${limit ?? 0} items`,
        },
      }));
    }

    setReadFromUrlDone(true);
  }, []);

  /**
   * Opens a modal right away if payout id is in the url.
   * Note: view is query variable corresponding with payout id.
   */
  const quickOpen = async () => {
    if (!view) return;

    const freshData = await fetchPayoutById(view);

    showModal(MODAL_TYPES.PAYOUT_DETAILED_VIEW, {
      payout: freshData?.data,
      refetch: refetch,
    });
  };

  useEffect(() => {
    quickOpen();
  }, []);

  useEffect(() => {
    if (readFromUrlDone) {
      fetch();
      addHistory();
    }
  }, [ordering, filters, pagination]);

  useEffect(() => {
    setPagination((prevPagination: any) => {
      return { ...prevPagination, offset: 0 };
    });
  }, [ordering, filters]);

  // Sets up filter options (for artist and requester) as soon as artists and admins are loaded.
  useEffect(() => {
    if (!isLoadingArtist && !isLoadingAdmins) getUsersData();
  }, [isLoadingArtist, isLoadingAdmins]);

  useEffect(() => {
    const FILTER_OPERATORS: any = {
      artistId: '==',
      status: '==',
      requestedBy: '==',
      createdAt: {
        startDate: '>=',
        endDate: '<=',
      },
    };

    const tmpFilters = Object.keys(filtersState)
      ?.map((key) => {
        return {
          value: filtersState[key]?.value,
          attribute: key,
          operation: FILTER_OPERATORS?.[key],
        };
      })
      .filter((item) => item?.value != undefined);

    if (filtersState?.createdAt?.startDate && filtersState?.createdAt?.endDate) {
      tmpFilters.push({
        value: Timestamp.fromDate(filtersState?.createdAt?.startDate),
        attribute: 'createdAt',
        operation: '>=',
      });

      const endDate: Date = filtersState?.createdAt?.endDate;
      endDate.setHours(23);
      endDate.setMinutes(59);
      endDate.setSeconds(59);

      tmpFilters.push({
        value: Timestamp.fromDate(endDate),
        attribute: 'createdAt',
        operation: '<=',
      });
    }

    setFilters(tmpFilters);
  }, [filtersState]);

  return (
    <div style={{ marginTop: '150px' }}>
      <Box margin="0 auto 30px">
        <FlexContainer flexDirection="row" flexWrap="wrap" justifyContent="space-between">
          <div style={{ maxWidth: '80%' }}>
            <FilterControle
              loading={isLoadingData}
              onChange={(item) => setFiltersState({ ...filtersState, [item.name]: item })}
              clear={(name: string) => setFiltersState({ ...filtersState, [name]: undefined })}
              values={filtersState}
              keys={[
                {
                  name: 'artistId',
                  placeholder: strings.SelectArtist,
                  options: filtersOptions?.artists ?? [],
                  disabled: !filtersOptions?.artists,
                },
                {
                  name: 'status',
                  placeholder: strings.SelectStatus,
                  options: STATUSES,
                },
                {
                  name: 'requestedBy',
                  placeholder: strings.SelectRequestor,
                  options: filtersOptions?.users ?? [],
                  disabled: !filtersOptions?.users,
                },
                {
                  name: 'createdAt',
                  placeholder: strings.DateRange,
                  options: [],
                  type: 'date-range',
                  onChange: (dates: [Date | null, Date | null]) => {
                    const start = dates['0'];
                    const end = dates['1'];

                    setFiltersState({
                      ...filtersState,
                      createdAt: { startDate: start, endDate: end },
                    });
                  },
                },
              ]}
            />
          </div>
          <Button
            height={60}
            width={150}
            onClick={() => showModal(MODAL_TYPES.CREATE_PAYOUT_MODAL, { refetch: refetch })}
          >
            <span style={{ fontSize: '16px' }}>{strings.CreatePayout}</span>
          </Button>
        </FlexContainer>
      </Box>
      <Box>
        {!isLoadingData && (
          <>
            <div style={{ marginBottom: '60px' }}>
              <PaginationControle
                setPagination={setPagination}
                pagination={{ ...pagination, amount: payouts?.amount ?? 0 }}
              />
            </div>
            <FlexebleTable
              ordering={ordering}
              data={payouts?.results}
              schema={[
                {
                  header: 'Id',
                  key: 'uid',
                  headerClick: () => manageOrdering('uid'),
                },
                {
                  header: strings.Status,
                  key: 'status',
                  mapper: (item) => {
                    return (
                      <StatusBadge className={item?.status ?? 'requested'}>
                        {item?.status ?? 'requested'}
                      </StatusBadge>
                    );
                  },
                  headerClick: () => manageOrdering('status'),
                  cellClick: (item) => {
                    setFiltersState({
                      ...filtersState,
                      status: STATUSES.find((itm) => itm?.value == item?.status),
                    });
                  },
                },
                {
                  header: strings.Artist,
                  key: 'artistId',
                  mapper: (item) => item?.artist?.Name,
                  headerClick: () => manageOrdering('artistId'),
                  cellClick: (item) => {
                    if (!filtersOptions?.artists) return;
                    setFiltersState({
                      ...filtersState,
                      artistId: filtersOptions?.artists?.find(
                        (art: any) => art?.value == item?.artistId,
                      ),
                    });
                  },
                },
                {
                  header: strings.RequestedBy,
                  key: 'requestedBy',
                  headerClick: () => manageOrdering('requestedBy'),
                  mapper: (item) =>
                    item?.requestedBy == item?.artistId
                      ? item?.artist?.Name
                      : item?.requester?.Name,
                  cellClick: (item) => {
                    if (!filtersOptions?.users) return;
                    setFiltersState({
                      ...filtersState,
                      requestedBy: filtersOptions?.users?.find(
                        (usr: any) => usr?.value == item?.requestedBy,
                      ),
                    });
                  },
                },
                {
                  header: strings.RequestedAt,
                  key: 'createdAt',
                  mapper: (item) => {
                    if (!item?.createdAt) return <></>;
                    return (
                      <>
                        {timeStampToDate(item?.createdAt)}
                        {tableStates?.hover?.includes(`${item?.uid}_createdAt`) && (
                          <CellHoverInfo>
                            {timeStampToTime(item?.createdAt)} {timeStampToDate(item?.createdAt)}
                          </CellHoverInfo>
                        )}
                      </>
                    );
                  },
                  headerClick: () => manageOrdering('createdAt'),
                  cellHoverEnter: (item) => setTableStates({ hover: [`${item?.uid}_createdAt`] }),
                  cellHoverLeave: () => setTableStates({ hover: [] }),
                  style: {
                    cell: {
                      cursor: 'help',
                    },
                  },
                },
                {
                  header: strings.RequestedAmount,
                  key: 'requestedAmount',
                  mapper: (item) => `$ ${(+item?.requestedAmount).toFixed(2)}`,
                  headerClick: () => manageOrdering('requestedAmount'),
                },
                {
                  header: strings.ApprovedAmount,
                  key: 'approvedAmount',
                  mapper: (item) =>
                    item?.approvedAmount
                      ? `$ ${(+item?.approvedAmount).toFixed(2)}`
                      : strings.WaitingOnApproval,
                  headerClick: () => manageOrdering('approvedAmount'),
                },
                {
                  header: strings.LastUpdateAt,
                  key: 'updatedAt',
                  mapper: (item) => {
                    return (
                      <>
                        {item?.updatedAt ? timeStampToDate(item?.updatedAt) : 'No updates yet'}
                        {item?.updatedAt && tableStates?.hover?.includes(`${item?.uid}_updatedAt`) && (
                          <CellHoverInfo>
                            {timeStampToTime(item?.updatedAt)} {timeStampToDate(item?.updatedAt)}
                          </CellHoverInfo>
                        )}
                      </>
                    );
                  },
                  headerClick: () => manageOrdering('updatedAt'),
                  cellHoverEnter: (item) => setTableStates({ hover: [`${item?.uid}_updatedAt`] }),
                  cellHoverLeave: () => setTableStates({ hover: [] }),
                  style: {
                    cell: {
                      cursor: 'help',
                    },
                  },
                },
                {
                  header: strings.PayoutMethod,
                  key: 'paymentMethod',
                  mapper: (item) => item?.paymentMethod ?? '-',
                  headerClick: () => manageOrdering('paymentMethod'),
                },
                {
                  header: strings.PaymentMethodEmail,
                  key: 'paymentMethodEmail',
                  mapper: (item) => item?.paymentMethodEmail ?? '-',
                  headerClick: () => manageOrdering('paymentMethodEmail'),
                },
                {
                  header: strings.Actions,
                  key: 'payoutMethod',
                  mapper: (item) => {
                    item;
                    return (
                      <ActionsControle
                        collapsed={collapseState?.[item?.uid] ?? false}
                        setCollapsed={(value) =>
                          setCollapseState({
                            ...collapseState,
                            [item?.uid]: value,
                          })
                        }
                      >
                        <Text
                          onClick={() => {
                            showModal(MODAL_TYPES.PAYOUT_DETAILED_VIEW, {
                              payout: item,
                              refetch: refetch,
                              onClose: () => addHistory(),
                            });
                            addHistory(item?.uid);
                          }}
                        >
                          {strings.ShowDetails}
                        </Text>
                        {item?.status == 'requested' && (
                          <Text
                            onClick={() =>
                              showModal(MODAL_TYPES.CONFIRMATION_MODAL, {
                                title: strings.ChangeStatusToPending,
                                content: () => {
                                  strings.AreYouSureYouWantToSetThisPayoutAsPending;
                                },
                                action: () => {
                                  statusChange(item?.uid, 'pending');
                                  return true;
                                },
                              })
                            }
                          >
                            {strings.StatusToPending}
                          </Text>
                        )}
                        {['pending', 'requested'].includes(item?.status) && (
                          <Text
                            onClick={() =>
                              showModal(MODAL_TYPES.CONFIRMATION_MODAL, {
                                title: strings.ChangeStatusToApproved,
                                content: () => {
                                  return (
                                    <>
                                      <Text style={{ width: '100%', padding: '10px' }}>
                                        {
                                          strings.ToMoveThisPayoutAsApprovedYouHaveToEnterApprovedAmount
                                        }
                                      </Text>
                                      <InputStyled
                                        placeholder={strings.EnterApprovedAmount}
                                        type="number"
                                        id="approved_amount"
                                        ref={approveAmountElem}
                                        defaultValue={item.requestedAmount}
                                      />
                                      <Text style={{ width: '100%', padding: '10px' }}>
                                        {strings.AreYouSureYouWantToSetThisPayoutAsApproved}
                                      </Text>
                                    </>
                                  );
                                },
                                action: () => {
                                  if (
                                    [0, null].includes(+(approveAmountElem as any)?.current?.value)
                                  ) {
                                    toast.error(strings.ApprovedAmountIsNotSet);
                                    return false;
                                  }

                                  statusChange(item?.uid, 'approved', {
                                    approvedAmount: +(approveAmountElem as any)?.current?.value,
                                  });
                                  return true;
                                },
                              })
                            }
                          >
                            {strings.StatusToApproved}
                          </Text>
                        )}
                        {['pending', 'requested'].includes(item?.status) && (
                          <Text
                            onClick={() =>
                              showModal(MODAL_TYPES.CONFIRMATION_MODAL, {
                                title: strings.ChangeStatusToRejected,
                                content: () => {
                                  return (
                                    <>
                                      <Text style={{ width: '100%', padding: '10px' }}>
                                        {strings.ToMoveThisPayoutAsRejectedYouHaveToEnterReasoning}
                                      </Text>
                                      <TextAreaStyled
                                        placeholder={strings.enterReasoning}
                                        width="100%"
                                        ref={reasoningElem}
                                      />
                                      <Text style={{ width: '100%', padding: '10px' }}>
                                        {strings.AreYouSureYouWantToSetThisPayoutAsRejected}
                                      </Text>
                                    </>
                                  );
                                },
                                action: () => {
                                  if (!(reasoningElem as any)?.current?.value) {
                                    toast.error(strings.ReasoningIsNotProvided);
                                    return false;
                                  }

                                  statusChange(item?.uid, 'rejected', {
                                    answer: (reasoningElem as any)?.current?.value,
                                    triggerEmailSend: true,
                                  });
                                  return true;
                                },
                              })
                            }
                          >
                            Status to Rejected
                          </Text>
                        )}
                        {item?.status == 'approved' && (
                          <Text
                            onClick={() =>
                              showModal(MODAL_TYPES.CONFIRMATION_MODAL, {
                                title: strings?.ChangeStatusToCompleted,
                                content: () => strings?.AreYouSureYouWantToSetThisPayoutAsCompleted,
                                action: () => {
                                  statusChange(item?.uid, 'completed');
                                  return true;
                                },
                              })
                            }
                          >
                            {strings?.StatusToCompleted}
                          </Text>
                        )}
                      </ActionsControle>
                    );
                  },
                  style: {
                    header: { textAlign: 'right', marginRight: '10px' },
                    cell: { justifyContent: 'end', marginRight: '10px' },
                  },
                },
              ]}
            />
          </>
        )}
        {isLoadingData && <Splash margin="40px 0 0 0" position="relative" />}
      </Box>
    </div>
  );
};

export default PayoutsScreen;
